/**
 * Cliente HTTP de la API.
 *
 * baseURL = import.meta.env.BASE_URL + "api" (mismo prefijo que strippea el
 * router PHP). Consistente con Vite `base` y el basename del router: por defecto
 * "/tembiapo/api"; con build a la raíz, "/api".
 *
 * Sesión:
 * - El **access token vive en memoria** (nunca en localStorage) → se pierde al
 *   recargar y se rehidrata con /auth/refresh (cookie httpOnly path=/tembiapo).
 * - `credentials: 'include'` en toda request para mandar la cookie de refresh.
 * - **Auto-refresh transparente ante 401** (una sola vez, con single-flight
 *   para no disparar múltiples refresh concurrentes). Si el refresh falla, se
 *   notifica `onSessionExpired` y se propaga el error original.
 *
 * Las rutas de auth (login/register/refresh/logout) pasan `skipAuthRefresh:true`
 * para NO entrar en el ciclo de refresh (un 401 de login es credenciales
 * inválidas, no una sesión vencida).
 */
import { API_BASE, IS_NATIVE } from './base';
import { saveRefreshToken } from './native/biometricAuth';

export { API_BASE };

// --- Estado de sesión en memoria --------------------------------------------
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}

// --- Refresh token en memoria (SÓLO nativo) ---------------------------------
// En la app nativa (Capacitor) las cookies httpOnly cross-origin no viajan
// confiables (WebView en https://localhost → API en tembiapoapp.com), así que el
// refresh se maneja token-based: se manda en el body de /auth/refresh. Mantenemos
// el token vigente en memoria (rota one-time-use en cada refresh) y lo
// persistimos en el Keychain/Keystore para sobrevivir reinicios. En web esto no
// se usa (el refresh vive sólo en la cookie httpOnly).
let nativeRefreshToken: string | null = null;

/** Fija el refresh token nativo vigente (tras login o desbloqueo biométrico). */
export function setNativeRefreshToken(token: string | null): void {
  nativeRefreshToken = token;
}
export function getNativeRefreshToken(): string | null {
  return nativeRefreshToken;
}

let onSessionExpired: (() => void) | null = null;
/** El AuthProvider registra acá cómo limpiar la sesión ante refresh fallido. */
export function setOnSessionExpired(cb: (() => void) | null): void {
  onSessionExpired = cb;
}

// --- Impersonación (soporte) ------------------------------------------------
// Mientras un super admin ve la app "como" una empresa usamos un access token
// efímero SIN cookie de refresh. Un 401 durante impersonación NO debe disparar
// el auto-refresh: usaría la cookie del super admin y volvería a su sesión sin
// aviso. En su lugar, avisamos que la impersonación caducó y la cortamos.
let impersonationActive = false;
/** El AuthProvider marca acá si hay una impersonación en curso. */
export function setImpersonationActive(active: boolean): void {
  impersonationActive = active;
}

let onImpersonationExpired: (() => void) | null = null;
/** Se invoca cuando el token de impersonación caduca (401 sin refresh). */
export function setOnImpersonationExpired(cb: (() => void) | null): void {
  onImpersonationExpired = cb;
}

// --- Errores ----------------------------------------------------------------
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    /** Código de negocio del contrato: 'invalid_credentials', 'email_taken', … */
    public readonly code: string,
    message: string,
    /** Errores por campo para 422 (validation). */
    public readonly fields?: Record<string, string>,
    /**
     * Objeto `error` crudo del contrato (además de code/message/fields). Lleva
     * datos extra de algunos errores de negocio, p.ej. `subtask_required_incomplete`
     * expone `{open, blocking:[{id,title,required_column_name}]}`.
     */
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  /** No intentar auto-refresh ante 401 (para endpoints de auth). */
  skipAuthRefresh?: boolean;
};

/** Respuesta común a login/register/refresh. */
export interface AuthTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: unknown;
  company?: unknown;
  /** Sólo presente en clientes nativos (token-based refresh). */
  refresh_token?: string;
}

async function doFetch(path: string, options: RequestOptions): Promise<Response> {
  const { body: rawBody, headers, skipAuthRefresh: _skip, ...rest } = options;
  const token = getAccessToken();

  let body = rawBody;

  // Nativo: /auth/refresh no puede depender de la cookie httpOnly cross-origin.
  // Inyectamos el refresh token guardado en el body (si el caller no lo puso).
  if (IS_NATIVE && path === '/auth/refresh') {
    const current = (body as { refresh_token?: unknown } | undefined)?.refresh_token;
    if (typeof current !== 'string' || current === '') {
      body = { ...(typeof body === 'object' && body ? body : {}), refresh_token: nativeRefreshToken ?? '' };
    }
  }

  // Uploads multipart: si el body es FormData NO seteamos Content-Type (el
  // browser pone el `boundary`) ni serializamos a JSON. Mantenemos Bearer y
  // credentials para preservar sesión y refresh transparente.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const wantsJsonBody = body !== undefined && !isFormData;

  const init: RequestInit = {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(wantsJsonBody ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      // Marca de cliente nativo: el backend expone el refresh en el body
      // (token-based) sólo cuando ve este header. En web nunca se envía.
      ...(IS_NATIVE ? { 'X-Native-Client': '1' } : {}),
      ...headers,
    },
    ...rest,
  };

  if (body !== undefined) {
    if (isFormData || typeof body === 'string') {
      init.body = body as BodyInit;
    } else {
      init.body = JSON.stringify(body);
    }
  }

  return fetch(`${API_BASE}${path}`, init);
}

async function parseOrThrow<T>(res: Response): Promise<T> {
  const isJson = res.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    const p = payload as { error?: unknown; fields?: unknown; message?: string } | null;
    // El contrato usa { error: "code", fields?: {...} }. Toleramos también el
    // formato anidado { error: { code, message } } por robustez.
    // La API usa el formato anidado { error: { code, message, fields } }.
    // Toleramos también { error: "code", fields } por robustez.
    let code = 'http_error';
    let message = `Error HTTP ${res.status}`;
    let nestedFields: unknown;
    // Objeto `error` crudo (cuando viene anidado) para exponer datos extra.
    let details: unknown;
    if (p && typeof p.error === 'string') {
      code = p.error;
      message = p.error;
    } else if (p && p.error && typeof p.error === 'object') {
      const e = p.error as { code?: string; message?: string; fields?: unknown };
      code = e.code ?? code;
      message = e.message ?? message;
      nestedFields = e.fields;
      details = e;
    }
    // fields puede venir dentro de error (contrato real) o al tope (fallback).
    const rawFields = nestedFields ?? p?.fields;
    const fields =
      rawFields && typeof rawFields === 'object'
        ? (rawFields as Record<string, string>)
        : undefined;
    throw new ApiError(res.status, code, message, fields, details);
  }

  return payload as T;
}

// --- Auto-refresh single-flight ---------------------------------------------
let refreshPromise: Promise<boolean> | null = null;

/** Un único refresh en vuelo; concurrentes comparten la misma promesa. */
function tryRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = doFetch('/auth/refresh', { method: 'POST', skipAuthRefresh: true })
      .then(async (res) => {
        if (!res.ok) return false;
        const data = (await res.json().catch(() => null)) as AuthTokenResponse | null;
        if (!data?.access_token) return false;
        setAccessToken(data.access_token);
        // Nativo: el refresh rotó (one-time-use). Guardamos el nuevo token en
        // memoria y en el Keychain/Keystore para el próximo refresh/reinicio.
        if (IS_NATIVE && typeof data.refresh_token === 'string' && data.refresh_token !== '') {
          setNativeRefreshToken(data.refresh_token);
          void saveRefreshToken(data.refresh_token);
        }
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let res = await doFetch(path, options);

  if (res.status === 401 && !options.skipAuthRefresh) {
    if (impersonationActive) {
      // Impersonando: NO refrescamos (la cookie es del super admin). El token
      // efímero caducó → cortamos la impersonación y propagamos el error.
      onImpersonationExpired?.();
    } else {
      const refreshed = await tryRefresh();
      if (refreshed) {
        // Reintento único con el nuevo token. Si vuelve a fallar, se propaga.
        res = await doFetch(path, options);
      } else {
        setAccessToken(null);
        onSessionExpired?.();
      }
    }
  }

  return parseOrThrow<T>(res);
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
};

/** Tipo de respuesta de GET /health. */
export interface HealthResponse {
  status: string;
  db: boolean;
  version: string;
  env: string;
  time: string;
}
